import React from "react";
import { AlertCircle, BookOpen, Check, Loader } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useWebsiteAnalysisToastStore } from "@/features/onboarding/stores/websiteAnalysisToastStore";
import SecondaryButton from "@/shared/components/ui/Button/SecondaryButton";
import FloatingProgressToast from "@/shared/components/ui/Toast/FloatingProgressToast";

const STEP_META = [
  { id: "read", title: "Reading website" },
  { id: "services", title: "Fetching your services" },
  { id: "summary", title: "Creating summary" },
];

export default function WebsiteAnalysisToastPanel({ onDismiss }) {
  const navigate = useNavigate();
  const {
    brandLabel,
    headerSubtitle,
    phases,
    progress,
    isDone,
    isError,
    errorMessage,
  } = useWebsiteAnalysisToastStore();

  const resolvedErrorMessage =
    errorMessage?.trim() ||
    "An error occurred while fetching company data. You can add details in settings.";

  const stepSubline = (meta, isComplete, isActive) => {
    if (isComplete) return "Done";
    if (!isActive) return "Waiting...";
    if (meta.id === "summary") return "Summary is being created...";
    return "~25 seconds";
  };

  const pct = Math.min(100, Math.max(0, Number(progress) || 0));
  const title = isDone
    ? "Company Profile Created"
    : brandLabel
      ? `Learning about ${brandLabel}`
      : "Learning about your company";
  const subtitle = isDone
    ? "Company profile created successfully"
    : isError
      ? resolvedErrorMessage
      : headerSubtitle;
  const icon = (
    <div
      className={`shrink-0 w-9 h-9 rounded-lg ${
        isDone ? "bg-success-500" : isError ? "bg-error-50" : "bg-[#DBF5F9]"
      } flex items-center justify-center`}
    >
      {isDone ? (
        <Check className="text-white" size={18} strokeWidth={2} />
      ) : isError ? (
        <AlertCircle className="text-error-500" size={18} strokeWidth={2} />
      ) : (
        <BookOpen className="text-secondary" size={18} strokeWidth={2} />
      )}
    </div>
  );
  const action =
    isDone || isError ? (
      <SecondaryButton
        title="View Details"
        onClick={() => {
          navigate("/settings/company_profile");
          onDismiss();
        }}
        className="py-1 px-2 text-[10px] rounded-md shadow-none"
      />
    ) : null;

  return (
    <FloatingProgressToast
      icon={icon}
      title={title}
      subtitle={subtitle}
      action={action}
      onDismiss={onDismiss}
    >
      {!isDone && !isError && (
        <>
          <ul className="px-3 py-2 max-h-[280px] overflow-y-auto">
            {STEP_META.map((meta, i) => {
              const status = phases[i] ?? "pending";
              const isComplete = status === "complete";
              const isActive = status === "active";
              const isPending = status === "pending";

              const subline = stepSubline(meta, isComplete, isActive);

              return (
                <li
                  key={meta.id}
                  className={`flex gap-2.5 py-1 ${i < STEP_META.length - 1 ? "border-b border-gray-50" : ""}`}
                >
                  <div className="shrink-0 w-6 flex justify-center pt-0.5">
                    {isComplete ? (
                      <span className="flex w-5 h-5 rounded-full bg-success-500 items-center justify-center">
                        <Check
                          className="text-white"
                          size={11}
                          strokeWidth={3}
                        />
                      </span>
                    ) : isActive ? (
                      <Loader
                        className="text-[#ADD2DC] animate-spin"
                        size={18}
                        strokeWidth={2}
                      />
                    ) : (
                      <Loader
                        className="text-[#ADD2DC] animate-spin opacity-55"
                        size={18}
                        strokeWidth={2}
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p
                      className={`text-xs font-medium leading-snug ${
                        isPending ? "text-gray-400" : "text-gray-900"
                      }`}
                    >
                      {meta.title}
                    </p>
                    <p
                      className={`text-[10px] mt-0.5 leading-relaxed ${
                        isComplete
                          ? "text-success-600"
                          : isActive
                            ? "text-gray-500"
                            : "text-gray-400"
                      }`}
                    >
                      {subline}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="px-4 pb-3 pt-0.5">
            <div className="h-1 w-full rounded-full bg-gray-200 overflow-hidden">
              <div
                className="h-full rounded-full bg-secondary transition-[width] duration-500 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </>
      )}
    </FloatingProgressToast>
  );
}
